import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../business/Business.css';
import './ReplenishPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';
import ProductsSearch from '../product/components/ProductsSearch';

function getQuantityToneClass(quantity) {
  if (!Number.isFinite(quantity) || quantity <= 0) return 'replenish-page-stock--red';
  if (quantity <= 5) return 'replenish-page-stock--orange';
  if (quantity <= 15) return 'replenish-page-stock--yellow';
  return 'replenish-page-stock--green';
}

function ReplenishPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { loading, requestWithMeta } = useHttp();

  const [submitError, setSubmitError] = React.useState(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(null);
  const [quantityById, setQuantityById] = React.useState({});
  const [isRental, setIsRental] = React.useState(false);

  const selectedCount = React.useMemo(() => {
    return Object.values(quantityById).filter((value) => {
      const parsed = Number(String(value ?? '').replace(',', '.').trim());
      return Number.isFinite(parsed) && parsed > 0;
    }).length;
  }, [quantityById]);
  const skeletonItems = React.useMemo(() => Array.from({ length: 4 }, (_, i) => i), []);

  const isSubmitDisabled = loading || selectedCount === 0;

  React.useEffect(() => {
    if (!branchId) {
      navigate('/branch/store', { replace: true });
    }
  }, [branchId, navigate]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await requestWithMeta('/operator/business', 'GET');
      if (cancelled || !res?.ok) return;
      const business = res?.data?.business ?? res?.data ?? null;
      setIsRental(Number(business?.typeId) === 2);
    })();
    return () => {
      cancelled = true;
    };
  }, [requestWithMeta]);

  async function handleSubmit() {
    setSubmitError(null);
    setSubmitSuccess(null);

    const selectedPayload = Object.entries(quantityById)
      .map(([id, value]) => {
        const parsed = Number(String(value ?? '').replace(',', '.').trim());
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        const productId = Number.parseInt(String(id), 10);
        if (!Number.isFinite(productId)) return null;
        return { productId, quantity: parsed };
      })
      .filter(Boolean);

    if (selectedPayload.length === 0) {
      setSubmitError('Укажите количество больше 0 хотя бы для одного товара');
      return;
    }

    const res = await requestWithMeta(`/operator/branches/${branchId}/products`, 'POST', {
      products: selectedPayload,
    });

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось пополнить товары');
      return;
    }

    setSubmitSuccess('Пополнение успешно отправлено');
    navigate(`/branch/${branchId}`, { replace: true });
  }

  if (!branchId) return null;

  return (
    <div className="shop-create-root replenish-page-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="Пополнение товаров"
          subtitle="Укажите количество для нужных товаров"
          onBack={() => navigate(`/branch/${branchId}`)}
        />

        <main className="shop-create-content">
          {!isRental ? (
          <button
            type="button"
            className="replenish-page-preOrderButton"
            onClick={() => navigate(`/branch/${branchId}/products/pre-order`)}
          >
            Товары на предзаказ
          </button>
          ) : null}

          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          {submitSuccess ? (
            <section className="shop-create-section replenish-page-sectionSuccess">
              <div className="shop-create-label">Готово</div>
              <div className="shop-create-help shop-create-helpTight">{submitSuccess}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <ProductsSearch
              flatten
              branchId={branchId}
              emptyText="Товары не найдены."
              toolbar={({ total, loading: productsLoading }) => (
                <div className="replenish-page-listHeader">
                  <div className="shop-create-label replenish-page-listTitle">Товары</div>
                  <div className="shop-create-help shop-create-helpTight">
                    {productsLoading
                      ? 'Загрузка…'
                      : selectedCount > 0
                        ? `Выбрано: ${selectedCount}`
                        : `${total} товаров`}
                  </div>
                </div>
              )}
            >
              {({ products, loading: productsLoading }) => (
                <div className="replenish-page-list">
                  {productsLoading && products.length === 0
                    ? skeletonItems.map((item) => (
                        <div key={item} className="replenish-page-item replenish-page-item--skeleton">
                          <div className="shop-create-skeleton replenish-page-skeletonImage" />
                          <div className="shop-create-skeleton shop-create-skeleton--text replenish-page-skeletonName" />
                          <div className="shop-create-skeleton replenish-page-skeletonInput" />
                        </div>
                      ))
                    : products.map((product) => {
                        const productId = product?._id ?? product?.id;
                        const quantity = quantityById[productId] ?? quantityById[String(productId)] ?? '';
                        const stockParsed = Number(product?.quantity);
                        const stockQty = Number.isFinite(stockParsed) ? stockParsed : 0;
                        const stockClassName = getQuantityToneClass(stockQty);

                        return (
                          <label key={productId} className="replenish-page-item">
                            <img
                              className="replenish-page-image"
                              src={product?.imageUrl || ''}
                              alt={product?.name || 'product'}
                              loading="lazy"
                            />

                            <div className="replenish-page-main">
                              <div className="replenish-page-name">{product?.name || 'Без названия'}</div>
                              <div className={`replenish-page-stock ${stockClassName}`}>
                                {stockQty} шт.
                              </div>
                            </div>

                            <input
                              type="number"
                              min="0"
                              step="1"
                              inputMode="numeric"
                              className="replenish-page-qtyInput"
                              value={quantity}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                setQuantityById((prev) => ({
                                  ...prev,
                                  [productId]: nextValue,
                                }));
                              }}
                              placeholder="0"
                              aria-label={`Количество для ${product?.name || 'товара'}`}
                            />
                          </label>
                        );
                      })}
                </div>
              )}
            </ProductsSearch>
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={loading ? 'Отправка…' : 'Пополнить'}
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}

export default ReplenishPage;
